import FileSaver from 'file-saver';
import { cloneDeep } from 'lodash';
import { Constants } from '@praktio/common';
import { Dispatch } from 'redux';

import { updateStatusForLearner, submitSelectionForLearner, getPDFForLearner } from '../utils/api';
import { update as updateSlide } from './slidesLitmosReducer';
import { update as updateQuiz } from './quizLitmosReducer';
import {
  determineQuizData,
  notifyError,
  setCompleted,
  showNextModule,
} from '../utils/learnerUtils';

const { TemplateTypes, ResourceItemTypes, AnnotationTypes } = Constants;

export const actions = {
  UPDATE: 'learner/update',
  CLEAR: 'learner/clear',
  LOAD: 'learner/load',
  SUCCESS: 'learner/success',
};

interface LearnerAction {
  type: typeof actions.UPDATE | typeof actions.CLEAR | typeof actions.LOAD | typeof actions.SUCCESS;
  payload: Learner;
}

type LearnerActionTypes = LearnerAction;

export const update = (payload: Learner) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

export const clear = () => {
  return {
    type: actions.CLEAR,
  };
};

export const load = () => {
  return {
    type: actions.LOAD,
  };
};

export const success = () => {
  return {
    type: actions.SUCCESS,
  };
};

export const updateStatus = (
  learnerId: string,
  slideId: string,
  lastActive: Date,
  isStart: boolean,
) => {
  return async () => {
    try {
      await updateStatusForLearner(learnerId, slideId, lastActive, isStart);
    } catch (e) {
      console.error(e);
    }
  };
};

export const getPDF =
  (learnerId: string, resource: SupplementalDoc) => async (dispatch: Dispatch) => {
    if (resource && resource.name && resource._id) {
      try {
        const name = resource.name.replace(' ', '_');
        const response = await getPDFForLearner(learnerId, resource._id);
        const blob = new Blob([response.data], { type: 'octet/stream' });
        const now = new Date()
          .toLocaleString()
          .replace(/ /g, '')
          .replace(/\//g, '-')
          .replace(/,/g, '_');
        FileSaver.saveAs(blob, `${name}-${now}.pdf`);
      } catch (e) {
        console.error(e);
      }

      return Promise.resolve();
    }
  };

interface Asset {
  correct: boolean;
  text: string;
  _id: string;
  subType: string;
  type: string;
}

export const submitForSlide = (
  learnerId: string,
  slide: Slide,
  selected: string | ISelected | ISelected[],
) => {
  return async (dispatch: Dispatch) => {
    dispatch(load());
    dispatch(update({ isSubmitting: true }));
    try {
      const { _id, type, paragraphSelection } = slide;
      const updatedSlide = cloneDeep(slide) as Slide;
      const slideResponse = await submitSelectionForLearner(learnerId, _id, selected);

      updatedSlide.learnerData = {
        ...updatedSlide.learnerData,
        ...slideResponse.data.learnerData,
      };

      const completed = updatedSlide.learnerData?.completed;
      if (completed) {
        if (type === TemplateTypes.ClickBestSpot && !paragraphSelection) {
          const correct = slideResponse.data.timeline.find(
            (a: Asset) => a.subType === AnnotationTypes.Correct,
          );
          if (correct) {
            updatedSlide.timeline.push(correct);
          }
        } else if (
          type === TemplateTypes.CheckAllApply ||
          type === TemplateTypes.ChooseBestProvision ||
          (type === TemplateTypes.ClickBestSpot && paragraphSelection)
        ) {
          slideResponse.data.timeline.forEach((a: Asset) => {
            if (
              a.type === ResourceItemTypes.Answer ||
              a.type === ResourceItemTypes.Provision ||
              a.subType === AnnotationTypes.Choice
            ) {
              const answer = updatedSlide.timeline.find((ua) => ua._id === a._id);
              if (answer) {
                answer.correct = a.correct;
                answer.text = a.text;
              }
            }
          });
        }
      }

      const allComplete = slideResponse.data.allComplete;
      const { isLast, learnerData } = updatedSlide;
      if (allComplete) {
        setCompleted();
      }

      if (isLast) {
        showNextModule({
          show: learnerData?.completed || type === TemplateTypes.Explanation,
          unlocked: learnerData?.unlocked,
          completed:
            learnerData?.completed || (learnerData?.unlocked && type === TemplateTypes.Explanation),
        });
      }

      const quizData = determineQuizData({ slide: updatedSlide });
      dispatch(updateSlide({ currentSlide: updatedSlide }));
      dispatch(updateQuiz(quizData));
    } catch (e) {
      notifyError(e);
    }
    dispatch(success());
    dispatch(update({ isSubmitting: false }));
  };
};

export const initialState: Learner = {
  learnerId: undefined,
  isSubmitting: false,
};

export const learnerReducer = (state = initialState, action: LearnerActionTypes) => {
  switch (action.type) {
    case actions.UPDATE:
      return {
        ...state,
        ...action.payload,
      };
    case actions.CLEAR:
      return initialState;
    default:
      return state;
  }
};
